import React, {useEffect, useState} from 'react'
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Drawer,
	IconButton,
	Typography,
} from '@mui/material'
import {Close, ContentCopy}         from '@mui/icons-material'
import {doGet}                      from '../../util/do-fetch'

/*
 * What the model was actually sent (097).
 *
 * Reads one capture from the server's in-memory buffer. The buffer is small and dies on restart, so
 * a 404 is ordinary rather than an error -- it means the capture aged out or the API restarted, and
 * the drawer says so plainly instead of showing a failure.
 */

const roleColor = {
	system: 'warning',
	user: 'info',
	assistant: 'success',
}

const Field = ({label, value}) => (
	<Box sx={{mr: 3, mb: 1}}>
		<Typography variant="caption" color="text.secondary" sx={{display: 'block'}}>{label}</Typography>
		<Typography variant="body2" sx={{fontFamily: 'monospace'}}>
			{value === null || value === undefined || value === '' ? '—' : String(value)}
		</Typography>
	</Box>
)

const Block = ({children}) => (
	<Box sx={{
		mt: .5, p: 1.5, borderRadius: 1,
		backgroundColor: 'action.hover',
		fontFamily: 'monospace', fontSize: '.78rem', lineHeight: 1.55,
		whiteSpace: 'pre-wrap', wordBreak: 'break-word',
		maxHeight: 420, overflowY: 'auto',
	}}>
		{children}
	</Box>
)

const PromptDebugDrawer = ({captureId, onClose}) => {
	const [capture, setCapture] = useState(null)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [copied, setCopied] = useState(false)

	useEffect(() => {
		if (!captureId) {
			setCapture(null)
			setError(null)
			return
		}

		let cancelled = false
		setLoading(true)
		setError(null)
		setCopied(false)

		doGet(`/mini/debug/prompt/${captureId}`)
			.then(async (response) => {
				if (cancelled) return
				if (response.status === 404) {
					setError('No capture for this message. It may have aged out of the buffer, or the API restarted since it was sent.')
					return
				}
				if (!response.ok) {
					setError(`Could not load the capture (${response.status}).`)
					return
				}
				setCapture(await response.json())
			})
			.catch(() => {
				if (!cancelled) setError('Could not reach the server for this capture.')
			})
			.finally(() => {
				if (!cancelled) setLoading(false)
			})

		return () => { cancelled = true }
	}, [captureId])

	const handleCopy = () => {
		navigator.clipboard.writeText(JSON.stringify(capture, null, 2))
			.then(() => setCopied(true))
			.catch(() => {})
	}

	const request = capture && capture.request
	const response = capture && capture.response

	return (
		<Drawer
			anchor="right"
			open={Boolean(captureId)}
			onClose={onClose}
			PaperProps={{sx: {width: {xs: '100%', sm: 720}, display: 'flex', flexDirection: 'column'}}}
		>
			<Box display="flex" alignItems="center" gap={1} sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
				<Typography variant="h6" sx={{flex: 1, fontSize: '1rem'}}>
					Sent to the model
				</Typography>
				{capture && (
					<Button size="small" startIcon={<ContentCopy/>} onClick={handleCopy}>
						{copied ? 'Copied' : 'Copy JSON'}
					</Button>
				)}
				<IconButton size="small" onClick={onClose}><Close fontSize="small"/></IconButton>
			</Box>

			<Box sx={{p: 2, flex: 1, overflowY: 'auto'}}>
				{loading && (
					<Box display="flex" alignItems="center" gap={1}>
						<CircularProgress size={16}/>
						<Typography variant="body2" color="text.secondary">Loading capture…</Typography>
					</Box>
				)}

				{error && !loading && <Alert severity="info">{error}</Alert>}

				{capture && !loading && (
					<>
						<Box display="flex" flexWrap="wrap" alignItems="flex-start">
							<Field label="Kind" value={capture.kind}/>
							<Field label="Model" value={request.model}/>
							<Field label="Temperature" value={request.temperature}/>
							<Field label="Max tokens" value={request.max_tokens}/>
						</Box>

						{response && (
							<Box display="flex" flexWrap="wrap" alignItems="center" gap={1} sx={{mb: 1}}>
								{/*
								  * finish_reason is the highest-value field here: 'length' means
								  * max_tokens truncated the reply, which from the outside looks
								  * exactly like a prompt that trails off.
								  */}
								{response.finishReason && (
									<Chip size="small"
									      color={response.finishReason === 'length' ? 'warning' : 'default'}
									      label={`finish: ${response.finishReason}`}/>
								)}
								{typeof response.durationMs === 'number' && (
									<Chip size="small" label={`${response.durationMs} ms`}/>
								)}
								{typeof response.chunkCount === 'number' && (
									<Chip size="small" label={`${response.chunkCount} chunks`}/>
								)}
								{response.error && <Chip size="small" color="error" label="request failed"/>}
							</Box>
						)}

						{response && response.finishReason === 'length' && (
							<Alert severity="warning" sx={{mb: 2}}>
								The reply hit the token ceiling and was cut off. That is CHAT_MAX_RESPONSE_TOKENS,
								not the wording of the prompt.
							</Alert>
						)}

						{capture.prompts && capture.prompts.length > 0 && (
							<>
								<Typography variant="subtitle2" sx={{mt: 2}}>Prompts used</Typography>
								<Box sx={{mt: .5}}>
									{capture.prompts.map(p => (
										<Box key={p.key} display="flex" alignItems="center" gap={1} sx={{py: .5}}>
											<Typography sx={{fontFamily: 'monospace', fontSize: '.78rem', flex: 1}}>
												{p.key}
											</Typography>
											{p.present
												? <Chip size="small" label={`v${p.version || 1}`}/>
												: <Chip size="small" color="error" label="missing"/>}
											{p.updated_by_email && (
												<Typography variant="caption" color="text.secondary">
													{p.updated_by_email}
												</Typography>
											)}
										</Box>
									))}
								</Box>
							</>
						)}

						<Divider sx={{my: 2}}/>

						<Typography variant="subtitle2">
							Messages ({request.messages.length})
						</Typography>
						{request.messages.map((m, i) => (
							<Box key={i} sx={{mt: 1.5}}>
								<Chip size="small" color={roleColor[m.role] || 'default'} label={m.role}/>
								<Block>{m.content}</Block>
							</Box>
						))}

						{response && (
							<>
								<Divider sx={{my: 2}}/>
								<Typography variant="subtitle2">Response</Typography>
								<Block>{response.error || response.body || '(empty)'}</Block>
							</>
						)}

						{capture.meta && (
							<>
								<Divider sx={{my: 2}}/>
								<Typography variant="subtitle2">Context</Typography>
								<Block>{JSON.stringify(capture.meta, null, 2)}</Block>
							</>
						)}
					</>
				)}
			</Box>
		</Drawer>
	)
}

export default PromptDebugDrawer
